import { Box, CssBaseline, useTheme } from "@mui/material";
import { useHomeRoute } from "hooks/useHomeRoute";
import { useScreenWidth } from "hooks/useScreenWidth";
import { QueryParamProvider } from "providers/queryParams";
import { Outlet } from "react-router-dom";

import { CommandPaletteProvider } from "./components/CommandPaletteProvider";
import { MinimizedDialogsToolbar } from "./components/MinimizedDialogs/MinimizedDialogsToolbar";
import { PrevNextNavigationProvider } from "./components/PrevNextNavigation";
import { SharedGoogleMapsProvider } from "./components/SharedGoogleMapsProvider";
import { AnalyticsIdentity } from "./instrumentation/AnalyticsIdentity";
import { BreadcrumbProvider } from "./providers/breadcrumbs";
import { DocumentAIAnalysisProvider } from "./providers/documentAIAnalysis";
import { DocumentReviewProvider } from "./providers/documentReview";
import { EncampThemeProvider } from "./providers/EncampThemeProvider";
import { MinimizedDialogsProvider } from "./providers/minimizedDialogs";
import { PreviewFeatureProvider } from "./providers/previewFeature";
import { SearchPillsProvider } from "./providers/searchPills";
import { ScoutConversationControllerProvider } from "./routes/Customer/Scout/hooks/scoutConversationControllerProvider";
import { OakStreamClientProvider } from "./stream";
import { useScopedOakStreamClient } from "./stream/useScopedOakStreamClient";

export function App() {
  const oakStreamClient = useScopedOakStreamClient();

  return (
    <QueryParamProvider>
      <SearchPillsProvider>
        <EncampThemeProvider>
          <CssBaseline />
          <MinimizedDialogsProvider>
            <DocumentReviewProvider>
              <DocumentAIAnalysisProvider>
                <CommandPaletteProvider>
                  <PrevNextNavigationProvider>
                    <PreviewFeatureProvider>
                      <BreadcrumbProvider>
                        <SharedGoogleMapsProvider>
                          <OakStreamClientProvider client={oakStreamClient}>
                            <ScoutConversationControllerProvider>
                              <AnalyticsIdentity />
                              <AppContent />
                              <MinimizedDialogsToolbar />
                            </ScoutConversationControllerProvider>
                          </OakStreamClientProvider>
                        </SharedGoogleMapsProvider>
                      </BreadcrumbProvider>
                    </PreviewFeatureProvider>
                  </PrevNextNavigationProvider>
                </CommandPaletteProvider>
              </DocumentAIAnalysisProvider>
            </DocumentReviewProvider>
          </MinimizedDialogsProvider>
        </EncampThemeProvider>
      </SearchPillsProvider>
    </QueryParamProvider>
  );
}

/**
 * Content shell rendered inside the theme provider so it can read the active
 * color scheme (`useThemeMode`) — `useColorScheme` only works inside the MUI
 * ThemeProvider.
 */
function AppContent() {
  const { screenMaxWidth } = useScreenWidth();
  const isHome = useHomeRoute();
  const theme = useTheme();
  const homeBackgroundColor = theme.palette.canvas;

  return (
    <Box
      sx={{
        // Home canvas behind the max-width column; topo texture is on the
        // full-viewport `HomeLinesBackdrop` in LayoutV2.
        backgroundColor: isHome ? homeBackgroundColor : undefined,
      }}
    >
      <Box sx={screenMaxWidth ? { maxWidth: screenMaxWidth } : {}}>
        <Outlet />
      </Box>
    </Box>
  );
}
